import React, { useState } from 'react'
import { Box, Button, Grid, TextField } from '@mui/material'
import { useForm } from 'react-hook-form'
import { UnderlyingCollateralCTO } from '@shared/CollateralSetCTO'
import { SectionHeader } from '@react/components/common/SectionHeader'

type Props = {
  collateral: UnderlyingCollateralCTO
  index: number | null
  onSave: (item: UnderlyingCollateralCTO, idx: number | null) => void
  onClose?: () => void
  poolAddr: string
}

export default function UnderlyingCollateralForm({ collateral, index, onSave, onClose }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UnderlyingCollateralCTO>({
    defaultValues: collateral,
  })

  const onSubmit = async (data: UnderlyingCollateralCTO) => {
    setIsSubmitting(true)
    // If maturityDate is blank, set to undefined
    const maturityDate = data.maturityDate && data.maturityDate.trim() !== '' ? data.maturityDate : undefined
    await onSave({ ...data, maturityDate }, index)
    if (onClose) onClose()
    setIsSubmitting(false)
  }

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e)
      }}
      sx={{ p: 2, position: 'relative' }}
    >
      {onClose && (
        <Button
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            minWidth: 'auto',
            p: 1,
          }}
        >
          ✕
        </Button>
      )}

      <SectionHeader>{index !== null ? 'Edit Collateral' : 'Add Collateral'}</SectionHeader>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('assetType', { required: 'Asset Type is required' })}
            label="Asset Type"
            required
            fullWidth
            error={!!errors.assetType}
            helperText={errors.assetType?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('assetId', { required: 'Asset ID is required' })}
            label="Asset ID"
            required
            fullWidth
            error={!!errors.assetId}
            helperText={errors.assetId?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('maturityDate', {
              validate: (value) => !value || /^\d{4}-\d{2}-\d{2}$/.test(value) || 'Date must be in YYYY-MM-DD format',
            })}
            label="Maturity Date"
            fullWidth
            error={!!errors.maturityDate}
            helperText={errors.maturityDate?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('marketValue', {
              required: 'Market Value is required',
              min: { value: 0, message: 'Market Value must be non-negative' },
            })}
            label="Market Value"
            required
            type="number"
            inputProps={{ step: '0.01' }}
            fullWidth
            error={!!errors.marketValue}
            helperText={errors.marketValue?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('yieldOrCoupon', {
              required: 'Yield/Coupon is required',
              valueAsNumber: true,
              min: { value: 0, message: 'Yield/Coupon must be non-negative' },
            })}
            label="Yield/Coupon (%)"
            required
            type="number"
            inputProps={{ step: '0.01' }}
            fullWidth
            error={!!errors.yieldOrCoupon}
            helperText={errors.yieldOrCoupon?.message}
          />
        </Grid>
      </Grid>

      <Button type="submit" variant="contained" fullWidth disabled={isSubmitting} sx={{ mt: 3, mb: 2 }}>
        {isSubmitting ? 'Saving...' : 'Save'}
      </Button>
    </Box>
  )
}
